/**
 * R6 — ConversationThread.jsx
 *
 * "Ping-pong assistito": mostra il thread di messaggi dell'offerta
 * (richiesta iniziale + risposte cliente) e consente all'operatore di
 * incollare nuove risposte del cliente; ogni risposta viene passata al
 * parser deterministico backend che popola le celle vuote.
 */
import { useCallback, useEffect, useState } from "react";
import {
  MessagesSquare,
  Send,
  Loader2,
  AlertTriangle,
  User,
  Package,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErrorDetail } from "../lib/api";

function formatTime(ts) {
  try {
    return new Date(ts).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function ConversationThread({
  offerId,
  onVociUpdate,
}) {
  const [thread, setThread] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [lastParsed, setLastParsed] = useState(null);

  const load = useCallback(async () => {
    if (!offerId) return;
    setLoading(true);
    try {
      const d = await api.conversationThread(offerId);
      setThread(d.thread || []);
    } catch {
      setThread([]);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async () => {
    if (!reply.trim() || !offerId) return;
    setSending(true);
    try {
      const res = await api.conversationReply(offerId, reply);
      setLastParsed({
        params: res.parsed_params,
        conflicts: res.conflicts,
      });
      setReply("");
      await load();
      const count = Object.keys(res.parsed_params || {}).length;
      if (count > 0) {
        toast.success(
          count === 1
            ? "1 parametro riconosciuto e applicato"
            : `${count} parametri riconosciuti e applicati`
        );
      } else {
        toast.info("Risposta registrata — nessun parametro riconosciuto");
      }
      if (res.voci && onVociUpdate) onVociUpdate(res.voci);
    } catch (e) {
      toast.error(formatErrorDetail(e));
    } finally {
      setSending(false);
    }
  };

  if (!offerId) return null;

  return (
    <section
      className="rounded-md"
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-subtle)",
      }}
      data-testid="conversation-thread"
    >
      <header
        className="flex items-center gap-1.5 px-2.5 py-1.5 border-b text-[11px] uppercase tracking-wide font-semibold"
        style={{
          borderColor: "var(--border-subtle)",
          color: "var(--text-secondary)",
        }}
      >
        <MessagesSquare
          className="h-3 w-3"
          style={{ color: "var(--accent-primary)" }}
        />
        Conversazione
        {thread.length > 0 && (
          <span
            className="ml-1 tq-cell-mono font-normal"
            style={{ color: "var(--text-muted)" }}
          >
            ({thread.length})
          </span>
        )}
      </header>

      {/* Thread messages */}
      <div
        className="px-2 py-2 space-y-1.5 max-h-[180px] overflow-y-auto"
        data-testid="thread-messages"
      >
        {loading && (
          <div
            className="text-[11px] flex items-center gap-1"
            style={{ color: "var(--text-muted)" }}
          >
            <Loader2 className="h-3 w-3 animate-spin" />
            Caricamento…
          </div>
        )}
        {!loading && thread.length === 0 && (
          <div
            className="text-[11px] italic text-center py-2"
            style={{ color: "var(--text-muted)" }}
          >
            Nessun messaggio ancora. Incolla qui sotto la risposta del cliente.
          </div>
        )}
        {!loading &&
          thread.map((m, i) => (
            <div
              key={i}
              className="rounded px-2 py-1.5 text-[11px]"
              style={{
                background:
                  m.role === "cliente" ? "#F0F9FF" : "var(--bg-subtle)",
                border:
                  m.role === "cliente"
                    ? "1px solid #BAE6FD"
                    : "1px solid var(--border-subtle)",
              }}
              data-testid={`thread-msg-${i}`}
            >
              <div
                className="flex items-center gap-1 text-[9px] uppercase tracking-wide mb-0.5"
                style={{ color: "var(--text-muted)" }}
              >
                <User className="h-2.5 w-2.5" />
                {m.role === "cliente" ? "Cliente" : m.role}
                <span className="mx-1">·</span>
                {formatTime(m.timestamp)}
              </div>
              <div style={{ color: "var(--text-primary)", lineHeight: 1.4 }}>
                {m.text.length > 220
                  ? m.text.slice(0, 220) + "…"
                  : m.text}
              </div>
            </div>
          ))}
      </div>

      {/* Last parsed params feedback */}
      {lastParsed && Object.keys(lastParsed.params || {}).length > 0 && (
        <div
          className="px-2.5 py-1.5 text-[10px] border-t"
          style={{
            borderColor: "var(--border-subtle)",
            background: "#F0FDF4",
            color: "#166534",
          }}
          data-testid="last-parsed"
        >
          <div className="flex items-center gap-1">
            <Package className="h-2.5 w-2.5" />
            Applicati: {Object.keys(lastParsed.params).join(", ")}
          </div>
          {Object.keys(lastParsed.conflicts || {}).length > 0 && (
            <div
              className="flex items-center gap-1 mt-0.5"
              style={{ color: "#B45309" }}
            >
              <AlertTriangle className="h-2.5 w-2.5" />
              Conflitti su celle già confermate: non sovrascritte.
            </div>
          )}
        </div>
      )}

      {/* Reply composer */}
      <div
        className="px-2 py-2 border-t space-y-1"
        style={{ borderColor: "var(--border-subtle)" }}
      >
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={2}
          placeholder="Incolla qui la risposta del cliente…"
          className="w-full text-[11px] p-1.5 rounded resize-none"
          style={{ border: "1px solid var(--border-subtle)" }}
          data-testid="thread-reply-input"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !reply.trim()}
          className="w-full inline-flex items-center justify-center gap-1 h-6 rounded text-[11px] font-medium text-white disabled:opacity-40"
          style={{ background: "var(--accent-primary)" }}
          data-testid="thread-send-btn"
        >
          {sending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Send className="h-3 w-3" />
          )}
          Invia testo
        </button>
      </div>
    </section>
  );
}
